import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099RInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-R instructions for Tax Year 2025"
          description="Learn how to fill out Form 1099-R for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report retirement plan distributions and IRAs"
          keywords=""
          canonical="resources/information-returns/form-1099-r-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-R</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-r-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-R at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-R,{' '}
                        </strong>{' '}
                        reports retirement and insurance-related distributions,
                        including pensions, annuities, IRAs, retirement plans,
                        profit-sharing plans, and insurance contract payments.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by January 31, and
                        the form must be filed with the IRS by February 28, if
                        filing on paper or March 31, if filing electronically.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions for Form
                        1099-R, including payer and recipient details,{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 1-2</strong>{' '}
                        (Gross Distribution and Taxable Amount),{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 3-9</strong>{' '}
                        (Capital Gain, Federal Tax Withheld, Employee
                        Contributions, Net Unrealized Appreciation, Distribution
                        Code, and IRA/SEP/SIMPLE Information), and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Boxes 10-19
                        </strong>{' '}
                        (State and Local Tax Withholding and Reporting
                        Information).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-R?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS Form 1099-R is a tax form used to report distributions
                  from
                </Typography>

                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Pensions</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Annuities</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Retirement or profit-sharing plans
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>IRAs</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Insurance contracts</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Other similar financial products
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Individuals who are retired or who left a job where they had a
                  retirement plan may receive these distributions. This helps
                  the IRS track retirement income and ensures that individuals
                  report them on their tax returns
                </Typography>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-R?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1099-R must be filed when distributions of $10 or more
                  are made from:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Retirement plans</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Certain insurance contracts</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The following entities are responsible for filing this form:
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Retirement plan administrators</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Financial institutions</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Insurance companies</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Government agencies</ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-R due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-R:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-R to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-R with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-R deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099rinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-R with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must share copies of Form 1099-R to recipients by
                  January 31st of the year following the relevant tax year. This
                  allows recipients to accurately file their tax returns on
                  time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> *the due date falls on the weekend or federal
                  holiday , it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-R can lead to IRS penalties. To avoid potential
                  penalties, payers should take steps to ensure that Form 1099-R
                  is filed and distributed both accurately and on time.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-R penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-R
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-R penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-R forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099rinstructions`}
                    >
                      E-file Form 1099-R
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-R Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-R.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-r-payers-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-payers-details-img.png" />
                  <img
                    src="/assets/images/form-1099-r-payers-details-img.png"
                    loading="lazy"
                    alt="Form 1099-r-payer details"
                    width={591}
                    height={348}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-recipient-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-recipient-details-img.png" />
                  <img
                    src="/assets/images/form-1099-r-recipient-details-img.png"
                    loading="lazy"
                    alt="Form 1099-r-recipient details"
                    width={591}
                    height={378}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Gross Distribution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-gross-distribution-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-gross-distribution-img.png" />
                  <img
                    src="/assets/images/form-1099-r-gross-distribution-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-1-gross distributions"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total distribution amount before any tax or other
                  deductions were withheld. Include:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 15 }}>Direct rollovers</li>
                  <li style={{ marginBottom: 15 }}>
                    IRA direct payments to employer plans
                  </li>
                  <li style={{ marginBottom: 15 }}>
                    Recharacterized IRA contributions
                  </li>
                  <li style={{ marginBottom: 15 }}>Roth IRA conversions</li>
                  <li style={{ marginBottom: 15 }}>
                    Premiums for life/other insurance paid by trustee/custodian
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also include distributions to participants from governmental
                  section 457(b) plans. However, for early redemptions of
                  certificate of deposit (CD) held in a trust, report only the
                  net amount distributed after penalties/fees.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2a: Taxable Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-taxable-amount-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-taxable-amount-img.png" />
                  <img
                    src="/assets/images/form-1099-r-taxable-amount-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-2a-taxable income"
                    width={245}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxable portion of the distribution in Box 2a.
                  However, if you cannot reasonably obtain the data to calculate
                  the taxable amount, leave this box blank.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter 0 (zero) in Box 2a for any of the following non-taxable
                  situations:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Direct rollovers from a qualified plan, 403(b), or 457(b)
                    plan to another such plan or traditional IRA (except IRA
                    revocations)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Direct rollovers from a Roth account to a Roth IRA
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Traditional, SEP, or SIMPLE IRA amounts directly transferred
                    to an employer plan
                  </li>
                  <li style={{ marginBottom: 12 }}>IRA recharacterizations</li>
                  <li style={{ marginBottom: 12 }}>
                    Non-taxable 1035 exchanges of insurance/annuity contracts
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Non-taxable charges against cash value to purchase qualified
                    long-term care insurance
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2b: Total Distribution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-total-distribution-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-total-distribution-img.png" />
                  <img
                    src="/assets/images/form-1099-r-total-distribution-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-2b-total distribution"
                    width={487}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  &quot;Taxable amount not determined&quot; checkbox:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check this box if you cannot determine the taxable amount of
                    the distribution.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    This often applies when the distribution includes after-tax
                    contributions or if it&apos;s unclear whether an exception
                    to the early distribution penalty applies.
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    If you check this box, you should leave Box 2a (Taxable
                    amount) blank.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  &quot;Total distribution&quot; checkbox:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check this box if this is the final distribution from the
                    plan or account.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    It indicates that no further distributions are expected from
                    this plan for this participant.
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    This is often used for lump-sum distributions or when an
                    account is being closed out entirely.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> You can check either, both, or neither of these
                  boxes depending on the situation. If you can determine the
                  taxable amount, enter it in Box 2a and do not check the
                  &quot;Taxable amount not determined&quot; box. <br />
                  The &quot;Total distribution&quot; box can be checked
                  regardless of whether you can determine the taxable amount.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Capital Gain (Included in Box 2a)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-captial-gain-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-captial-gain-img.png" />
                  <img
                    src="/assets/images/form-1099-r-captial-gain-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-3-capital gain (included in box 2a)"
                    width={245}
                    height={118}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If any portion of the taxable amount in Box 2a is taxable as a
                  capital gain, enter that capital gain amount in Box 3.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For charitable gift annuities:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Report the total annuity distribution for the year in Box 1
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Report the taxable portion in Box 2a
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Report any capital gain amount in Box 3
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Report any nontaxable amount in Box 5
                  </li>
                  <li style={{ marginBottom: 12 }}>Enter Code F in Box 7</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Advise the annuity recipient if any amount in Box 3 is:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Subject to the 28% rate for collectibles
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    An unrecaptured section 1250 gain
                  </li>{' '}
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-federal-income-tax-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-federal-income-tax-img.png" />
                  <img
                    src="/assets/images/form-1099-r-federal-income-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-r-bosx-4-federal income tax withheld"
                    width={245}
                    height={118}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any federal income tax withheld from payments reported.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Employee Contributions/Designated Roth Contributions or
                  Insurance Premiums
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-employee-contribution-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-employee-contribution-img.png" />
                  <img
                    src="/assets/images/form-1099-r-employee-contribution-img.png"
                    loading="lazy"
                    alt="Form 1099-r- box-5- employee contributions/designated roth contributions or insurance premiums"
                    width={245}
                    height={117}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any of the following amounts the employee can receive
                  tax-free this year, even if they exceed the amount in Box 1:
                </Typography>
                <ul style={{ marginBottom: 30, listStyleType: 'lower-alpha' }}>
                  <li style={{ marginBottom: 12 }}>
                    Designated Roth account contributions or after-tax
                    contributions made by/for the employee in prior years
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Employer contributions considered as employee contributions
                    under section 72(f)
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Cost of life insurance protection premiums taxed to the
                    employee in previous years (only if the life insurance
                    contract itself is distributed)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Premiums paid for commercial annuities
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  DO NOT include:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Designated Roth account contributions already taxed
                  </li>
                  <li style={{ marginBottom: 12 }}>Elective deferrals</li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Contributions to a retirement plan that were not after-tax
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Net Unrealized Appreciation (NUA) in Employer&apos;s
                  Securities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-net-unrealized-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-net-unrealized-img.png" />
                  <img
                    src="/assets/images/form-1099-r-net-unrealized-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-6-unrealized appreciation in employer’s securities"
                    width={245}
                    height={118}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this box if you received a distribution from a qualified
                  plan, excluding a designated Roth account, that includes
                  securities from your employer&apos;s corporation, subsidiary,
                  or parent company, and you can calculate the Net Unrealized
                  Appreciation (NUA) in these securities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the distribution is a lump-sum, enter the total NUA in
                  employer securities. If it&apos;s not a lump-sum distribution,
                  only enter the NUA in employer securities that stem from
                  employee contributions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Distribution Code(s)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-distribution-code-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-distribution-code-img.png" />
                  <img
                    src="/assets/images/form-1099-r-distribution-code-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-7-distribution codes"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box labeled IRA/SEP/SIMPLE if the distribution comes
                  from a traditional IRA, SEP IRA, or SIMPLE IRA. Do not check
                  this box for distributions from a Roth IRA or for an IRA
                  recharacterization.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the relevant code(s) in box 7. Use Table 1 to find the
                  correct code(s) for any amounts listed on Form 1099-R. Pay
                  close attention to the codes and enter them accurately because
                  the IRS relies on them to determine if the distribution has
                  been properly reported. Incorrect codes could lead to
                  incorrect tax assessments by the IRS.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Other
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-box8-other-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-box8-other-img.png" />
                  <img
                    src="/assets/images/form-1099-r-box8-other-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-8-other"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the current value of an annuity contract within a
                  lump-sum distribution. Exclude this value from boxes 1 and 2a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To calculate the annuity contract value, determine the current
                  actuarial value, subtracting the excess of the employee&apos;s
                  contributions over the distributed cash and property
                  (excluding the annuity).
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the annuity is part of a lump-sum distribution for multiple
                  recipients, also enter the percentage of the total annuity
                  represented by each Form 1099-R in box 8. Additionally, record
                  any reductions in investment value against the cash value of
                  an annuity or life insurance contract due to charges or
                  payments for qualified long-term care insurance in box 8.
                  Ensure this value is not below zero.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9a: Your Percentage of Total Distribution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-box9a-percentage-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-box9a-percentage-img.png" />
                  <img
                    src="/assets/images/form-1099-r-box9a-percentage-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-9a-your percentage of total distribution"
                    width={244}
                    height={61}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  For a total distribution made to multiple individuals, enter
                  the percentage received by the person listed on Form 1099-R.
                  You don&apos;t need to fill out this box for IRA distributions
                  or direct rollovers.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9b: Total Employee Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-box9b-total-employee-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-box9b-total-employee-img.png" />
                  <img
                    src="/assets/images/form-1099-r-box9b-total-employee-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-9b-total employee contributions"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  It&apos;s optional to enter the total employee contributions
                  or designated Roth contributions in this box. However,
                  providing this information might be useful for the recipient.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you decide to report the total employee contributions or
                  designated Roth contributions, don&apos;t include any amounts
                  already recovered tax-free in previous years. If it&apos;s a
                  total distribution, report these contributions in box 5
                  instead of box 9b.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Amount Allocable to IRR Within 5 Years
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-amount-allocable-irr-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-amount-allocable-irr-img.png" />
                  <img
                    src="/assets/images/form-1099-r-amount-allocable-irr-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-10-amount allocable to irr within 5 years"
                    width={280}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Write down the portion of the distribution that relates to an
                  Individual Retirement Account (IRR) made within the five-year
                  period starting from the first day of the year of the
                  rollover. DO NOT fill out this box if an exception under
                  section 72(t) is applicable.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: First Year of Designated Roth Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-first-year-designated-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-first-year-designated-img.png" />
                  <img
                    src="/assets/images/form-1099-r-first-year-designated-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-11-first year of designated roth contributions"
                    width={175}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the first year of the five-tax-year period. This is the
                  year when the recipient initially established the designated
                  Roth account.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: FATCA Filing Requirement Checkbox
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-filling-requirement-checkbox-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-filling-requirement-checkbox-img.png" />
                  <img
                    src="/assets/images/form-1099-r-filling-requirement-checkbox-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-12-fatca filing requirement checkbox"
                    width={141}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if you are a
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 13 }}>
                    Financial Institution (FFI) reporting a U.S. account, such
                    as a cash value insurance or annuity contract, following the
                    requirements of section 6047(d).
                  </li>
                  <li style={{ marginBottom: 13 }}>
                    U.S. payer reporting on Form 1099-R to fulfill your
                    obligation for reporting on a U.S. account for chapter 4
                    purposes, as outlined in Regulations section
                    1.1471-4(d)(2)(iii)(A).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Date of Payment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-date-of-payment-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-date-of-payment-img.png" />
                  <img
                    src="/assets/images/form-1099-r-date-of-payment-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-13-date of payment"
                    width={141}
                    height={90}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter here the date payment was made for reportable death
                  benefits under section 6050Y.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 14–16: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-box14-16-state-information-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-box14-16-state-information-img.png" />
                  <img
                    src="/assets/images/form-1099-r-box14-16-state-information-img.png"
                    loading="lazy"
                    alt="Form 1099-r-box-14-16-state information"
                    width={676}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 14:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>Box 15:</b> Enter the 2-letter state abbreviation and
                  payer&apos;s ID number for that state
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 16:</b> Enter the amount of the state distribution
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 17–19: Local tax information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-r-local-tax-information-img.webp" />
                  <source srcSet="/assets/images/form-1099-r-local-tax-information-img.png" />
                  <img
                    src="/assets/images/form-1099-r-local-tax-information-img.png"
                    loading="lazy"
                    alt="Form 1099-r- box-17-19-local tax information"
                    width={676}
                    height={90}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 17:</b> Report any local income tax withheld from the
                  payment
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>Box 18:</b> Enter the name of the locality
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 19:</b> Enter the amount of the local distribution
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-R
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-R
                  </Link>
                  , either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-R
                  </Link>{' '}
                  and complete your filing within minutes. Here’s how it works:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s how to e-file Form 1099-R:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Distribution details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form to the IRS securely.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Share a copy to the recipient.
                  </li>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it. Here are the steps for paper filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-R from the official IRS website (
                    <Link
                      href="https://www.irs.gov"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      www.irs.gov
                    </Link>
                    ).
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink. This includes:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Distribution details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The address varies based on
                    your state. Refer to the Where to send{' '}
                    <Link
                      href="#1099-r-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-R - Mailing address
                    </Link>{' '}
                    section to get the address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by February 15th of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing
                  </Typography>
                  <ul style={{ marginBottom: 4 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms earlier before the deadline to ensure timely
                      arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                    id="1099-r-mailing-address"
                  >
                    file Form 1099-R electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-R - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-R varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-R:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-R to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-R with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-R
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-R:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Payer details</li>
                  <li style={{ marginBottom: 16 }}>Recipient details</li>
                  <li style={{ marginBottom: 16 }}>
                    Gross distribution amount
                  </li>
                  <li style={{ marginBottom: 16 }}>Taxable amount</li>
                  <li style={{ marginBottom: 16 }}>
                    Federal income tax withheld
                  </li>
                  <li style={{ marginBottom: 16 }}>Type of distribution</li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-R using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, mt: 1.5 }}>
                  <b>Step 1: Complete Form 1099-R</b>
                  <br />
                  Open Form 1099-R and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-R to
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, share a
                  copy of the completed Form 1099-R to the recipient.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-R with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  E-filing Form 1099-R with TaxZerone offers several advantages.
                  Here are some of the best benefits:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS Form Validations:
                      </span>{' '}
                      Automated checks for your returns for errors or missing
                      information to ensure accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email Recipient Copies:
                      </span>{' '}
                      Share recipient copies of Form 1099-R by email and
                      eliminate the need for manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure Portal Access (ZeroneVault):
                      </span>{' '}
                      Let your recipients access their return copies anytime,
                      anywhere through the secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive Pricing:
                      </span>{' '}
                      Industry-leading pricing, with rates starting as low as
                      $0.59 per form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By e-filing with TaxZerone, you can streamline your Form
                  1099-R filing, save time, reduce errors, and provide a better
                  experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-R.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099rinstructions`}
              >
                E-file Form 1099-R
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099RInstructions;
